import {
  CodeIcon,
  ElementEmbedIcon as EmbedIcon,
  ElementVideoIcon as VideoIcon,
  ElementYouTubeIcon as YouTubeIcon,
  AddPanelVideoUrl64Icon,
  AddPanelEmbed64Icon,
  YoutubeAdd64Icon,
} from '@packages/systems/spring';
import {identity} from '@packages/utilities/fp/utils';
import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';
import HtmlEmbed from './HtmlEmbed';
import Video from './Video';
import YouTubeVideo from './YouTubeVideo';
import {VideoPluginType} from './VideoPluginType';
import EmbedPluginType from './EmbedPluginType';
import {CodePluginType} from './CodePluginType';
import YouTubeVideoType from './datatypes/YouTubeVideoType';
import YouTubeOptionsType from './datatypes/YouTubeOptionsType';

import {
  Plugins,
  ElementPreset,
  Component,
  EnvTypes,
} from '@packages/systems/wfdl';
import {
  type Qualified,
  iconName,
  pluginName,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';

import {
  EElement,
  EText,
  ERecord,
  EBoolean,
  ENumber,
  ELiteral,
  EDistinct,
  TTypeConstructor,
  TText,
} from '@packages/systems/wfdl-ast';
import {tyfun} from '@packages/utilities/wfdl-helpers';
import {
  AutoplayTooltip,
  PrivacyModeTooltip,
} from './datatypes/YouTubeOptionsTooltipType';
import {
  getVideoUrl,
  videoIsSet,
  videoIsNotSet,
} from './videoConditionOperators';
import {operand} from '@packages/systems/plugins/ConditionsOperand';

const HtmlEmbedAddPanelIcon = wrapAddPanelIcon(AddPanelEmbed64Icon);
const VideoAddPanelIcon = wrapAddPanelIcon(AddPanelVideoUrl64Icon);
const YouTubeAddPanelIcon = wrapAddPanelIcon(YoutubeAdd64Icon);
const embedPluginName = pluginName('Embed');
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const name = <nameType extends unknown>(n: nameType): Qualified<nameType> =>
  qualify(embedPluginName, n);

const TVideo = TTypeConstructor(name(typeName('Video')));
const TCodeProp = TTypeConstructor(name(typeName('CodeProp')));

const videoToUrl: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: tyfun([TVideo], TText),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value: getVideoUrl,
};

const codeToText: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: tyfun([TCodeProp], TText),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value: identity,
};

export const plugin = Plugins.PrivilegedPlugin({
  atoms: {HtmlEmbed, Video, YouTubeVideo},
  icons: {
    HtmlEmbed: EmbedIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    HtmlEmbed_Medium: HtmlEmbedAddPanelIcon,
    YouTube: YouTubeIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    YouTube_Medium: YouTubeAddPanelIcon,
    Video: VideoIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    Video_Medium: VideoAddPanelIcon,
    Code: CodeIcon,
  },
  elementPresets: {
    HtmlEmbed: ElementPreset.create({
      factory: EElement({
        id: 'HTML Embed',
        type: qualify(Plugins.name('Embed'), Component.name('HtmlEmbed')),
        data: ERecord({
          search: ERecord({
            exclude: EBoolean(true),
          }),
          embed: ERecord({
            type: EText('html'),
            meta: ERecord({
              html: EText(''),
              div: EBoolean(false),
              script: EBoolean(false),
              compilable: EBoolean(false),
              iframe: EBoolean(false),
            }),
          }),
          insideRTE: EBoolean(false),
          value: EText(''),
          content: EDistinct({
            name: qualify(Plugins.name('Embed'), typeName('CodeProp')),
            value: EText(''),
          }),
        }),
      }),
      icon: {
        small: qualify(Plugins.name('Embed'), iconName('HtmlEmbed')),
        medium: qualify(Plugins.name('Embed'), iconName('HtmlEmbed_Medium')),
      },
      label: 'Code Embed',
      helpData: {
        description: [
          'The Code Embed component allows you to get super unique with custom HTML code. When you’re ready to see your <script> in action, publish or export your site.',
          'PS: You can only access this feature with a paid Webflow account or active site plan.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/embed.b6fa31ba3e.mp4',
      },
      enableIfFlag: 'allowCustomCode',
      needsUpgrade: {
        relativeUpgradeUrl: ({
          siteSlug,
        }: ElementPreset.ElementPresetUpgradeLinkProps) => {
          if (siteSlug === undefined) {
            return '/pricing';
          }

          return `/dashboard/sites/${siteSlug}/hosting?ref=add_embed`;
        },
        disabledMessage:
          'This element requires a paid site or account plan. Click the star icon to upgrade your plan.',
      },
      category: 'Custom Code',
    }),
    Video: ElementPreset.create({
      factory: EElement({
        id: 'Video',
        type: qualify(Plugins.name('Embed'), Component.name('Video')),
        data: ERecord({
          video: ELiteral({
            name: qualify(Plugins.name('Embed'), typeName('Video')),
            value: {embed: {type: 'video', url: '', title: ''}},
          }),
        }),
      }),
      helpData: {
        tag: 'div',
        description: [
          'Embed a video from a third-party site, like YouTube or Vimeo, by inputting its URL in this field.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/video.7a4e3baf83.mp4',
      },
      icon: {
        small: qualify(Plugins.name('Embed'), iconName('Video')),
        medium: qualify(Plugins.name('Embed'), iconName('Video_Medium')),
      },
      label: 'Video',
      category: 'Media',
      addTabIndex: 2,
    }),
    YouTubeVideo: ElementPreset.create({
      icon: {
        small: qualify(Plugins.name('Embed'), iconName('YouTube')),
        medium: qualify(Plugins.name('Embed'), iconName('YouTube_Medium')),
      },
      label: 'YouTube',
      factory: EElement({
        id: 'You Tube Video',
        type: qualify(Plugins.name('Embed'), Component.name('YouTubeVideo')),
        data: ERecord({
          search: ERecord({
            exclude: EBoolean(true),
          }),
          embed: ERecord({
            meta: ERecord({
              videoId: EText(''),
              aspectRatio: ENumber(0),
              url: EText(''),
              title: EText(''),
            }),
            options: ERecord({
              showAllRelatedVideos: EBoolean(false),
              controls: EBoolean(true),
              autoplay: EBoolean(false),
              muted: EBoolean(false),
              privacyMode: EBoolean(false),
              startAt: ENumber(0),
            }),
          }),
        }),
      }),
      helpData: {
        description: [
          'Embed YouTube videos with all the same display options offered by YouTube’s embed feature.',
          'This can include related videos, specific start times, show/hide video controls, and more.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/Youtube.21bc3887d1.mp4',
      },
      category: 'Media',
      addTabIndex: 3,
    }),
  },
  dataTypes: {
    Video: VideoPluginType,
    Code: EmbedPluginType,
    CodeProp: CodePluginType,
    YouTubeVideo: YouTubeVideoType,
    YouTubeOptions: YouTubeOptionsType,
    AutoplayTooltip,
    PrivacyModeTooltip,
  },
  values: {
    videoCondition: operand(TVideo),
    videoIsSet,
    videoIsNotSet,
    videoToUrl,
    codeToText,
  },
});
